/* eslint-disable no-throw-literal */
import gql from 'graphql-tag';
import apolloClient from '../../../../utils/apolloClient';

// ─── GraphQL Query ────────────────────────────────────────────────────────────

const ORDER_INVOICE_SUMMARY = gql`
  query OrderInvoiceSummary($input: SearchInput) {
    order_invoice_summary(input: $input) {
      application_fee
      card_fees
      company_id
      company_invoice_id
      credit_card_fees
      delivery_charge
      invoice_number
      kravein_fee
      kravein_charges
      kravein_payout
      net_payout
      order_count
      order_total
      pdf_s3_key
      payment_date
      sale_value
      status
      sub_total_gst
      total_orders
      total_places
      places {
        application_fee
        credit_card_fees
        delivery_fee
        kravein_fees
        net_payout
        order_count
        order_subtotal
        place_id
        place_name
        sale_value
        sub_total_gst
      }
      period {
        from
        to
      }
      company {
        abn
        address_line_1
        city
        company_id
        name
        post_code
        state
        contact {
          display
          display_order
          is_primary
          type
          value
        }
      }
      error {
        code
        description
      }
    }
  }
`;

// ─── Runtime Validators ───────────────────────────────────────────────────────

const INVOICE_STATUSES = new Set([
  'GENERATED',
  'SENT',
  'PAID',
  'DRAFT',
  'OVERDUE',
]);

const assertInvoiceStatus = (val, invoiceId) => {
  if (!val || !INVOICE_STATUSES.has(val)) {
    console.warn(
      `[orderInvoiceSummaryService] Unexpected status: "${val}" for "${invoiceId}", defaulting to GENERATED`,
    );
    return 'GENERATED';
  }
  return val;
};

// ─── Pure Transformers ────────────────────────────────────────────────────────

const toContact = raw => ({
  display: raw?.display ?? false,
  displayOrder: raw?.display_order ?? '',
  isPrimary: raw?.is_primary ?? false,
  type: raw?.type ?? '',
  value: raw?.value ?? '',
});

const toCompany = raw => ({
  companyId: raw?.company_id ?? '',
  name: raw?.name ?? '',
  abn: raw?.abn ?? null,
  addressLine1: raw?.address_line_1 ?? '',
  city: raw?.city ?? '',
  state: raw?.state ?? '',
  postCode: raw?.post_code ?? '',
  contacts: Array.isArray(raw?.contact)
    ? raw.contact.filter(c => c !== null).map(toContact)
    : [],
});

const toPeriod = raw => {
  const toRaw = raw?.to ?? '';
  let to = toRaw;
  if (toRaw) {
    const d = new Date(toRaw);
    d.setUTCDate(d.getUTCDate() - 1);
    to = d.toISOString().replace(/\.\d{3}Z$/, '.000Z');
  }
  return {
    from: raw?.from ?? '',
    to,
  };
};

const toPlaceSummary = raw => ({
  placeId: raw?.place_id ?? 'unknown',
  placeName: raw?.place_name ?? '',
  orderCount: raw?.order_count ?? 0,
  orderSubtotal: raw?.order_subtotal ?? 0,
  saleValue: raw?.sale_value ?? 0,
  kraveinFees: raw?.kravein_fees ?? 0,
  creditCardFees: raw?.credit_card_fees ?? 0,
  deliveryFee: raw?.delivery_fee ?? 0,
  netPayout: raw?.net_payout ?? 0,
  subTotalGst: raw?.sub_total_gst ?? 0,
  applicationFee: raw?.application_fee ?? 0,
});

const toOrderInvoiceSummary = raw => {
  const invoiceId = raw?.company_invoice_id ?? 'unknown';
  return {
    companyInvoiceId: invoiceId,
    companyId: raw?.company_id ?? '',
    invoiceNumber: raw?.invoice_number ?? '',
    status: assertInvoiceStatus(raw?.status, invoiceId),
    orderCount: raw?.order_count ?? 0,
    totalOrders: raw?.total_orders ?? 0,
    totalPlaces: raw?.total_places ?? 0,
    orderTotal: raw?.order_total ?? 0,
    saleValue: raw?.sale_value ?? 0,
    kraveinFee: raw?.kravein_fee ?? 0,
    kraveinCharges: raw?.kravein_charges ?? 0,
    cardFees: raw?.card_fees ?? 0,
    creditCardFee: raw?.credit_card_fees ?? 0,
    deliveryCharge: raw?.delivery_charge ?? 0,
    kraveinPayout: raw?.kravein_payout ?? 0,
    netPayout: raw?.net_payout ?? 0,
    subTotalGst: raw?.sub_total_gst ?? 0,
    applicationFee: raw?.application_fee ?? 0,
    pdfS3Key: raw?.pdf_s3_key ?? null,
    paymentDate: raw?.payment_date ?? null,
    period: toPeriod(raw?.period),
    company: toCompany(raw?.company),
    places: Array.isArray(raw?.places)
      ? raw.places.filter(p => p !== null).map(toPlaceSummary)
      : [],
  };
};

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const isAppError = err =>
  typeof err === 'object' && err !== null && 'code' in err && 'message' in err;

const toAppError = err => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('unauthorized') || msg.includes('unauthenticated')) {
      return { message: err.message, code: 'UNAUTHORIZED' };
    }
    if (msg.includes('network') || msg.includes('failed to fetch')) {
      return { message: err.message, code: 'NETWORK_ERROR' };
    }
    return { message: err.message, code: 'UNKNOWN_ERROR' };
  }
  return { message: 'An unexpected error occurred', code: 'UNKNOWN_ERROR' };
};

// ─── Service Implementation ───────────────────────────────────────────────────

export async function getOrderInvoiceSummary(
  params,
  client = apolloClient.clientPrivate,
) {
  const { userId, companyInvoiceId } = params;

  try {
    const { data } = await client.query({
      query: ORDER_INVOICE_SUMMARY,
      variables: {
        input: {
          user_id: userId,
          company_invoice_id: companyInvoiceId,
        },
      },
      fetchPolicy: 'network-only',
    });

    const result = data?.order_invoice_summary;

    if (!result) {
      throw {
        message: 'No data returned from order_invoice_summary',
        code: 'PARSE_ERROR',
      };
    }

    if (result.error?.length) {
      throw {
        message: result.error[0]?.description ?? 'API error',
        code: 'API_ERROR',
      };
    }

    return toOrderInvoiceSummary(result);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

// ─── Export Pure Transformers ─────────────────────────────────────────────────

export { toOrderInvoiceSummary, toPlaceSummary };

// ─── Export Service ───────────────────────────────────────────────────────────

export const orderInvoiceSummaryService = {
  getOrderInvoiceSummary,
};
